import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DrawerModule } from 'primeng/drawer';
import { TabsModule } from 'primeng/tabs';
import { TagModule } from 'primeng/tag';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { InputTextModule } from 'primeng/inputtext';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { AuthService } from '../../../core/services/api/auth.service';

export interface ScreenElement {
    type: 'toolbar' | 'table' | 'filter' | 'form' | 'modal' | 'status';
    title: string;
    requiredRoles?: string[];
    items: { label: string; icon?: string; description: string }[];
}

export interface ManualProcess {
    title: string;
    icon: string;
    steps: string[];
    tip?: string;
    warn?: string;
    requiredRoles?: string[];
}

export interface ManualSection {
    key: string;
    icon: string;
    title: string;
    subtitle: string;
    colorVar: string;
    requiredRoles: string[];
    screenElements: ScreenElement[];
    processes: ManualProcess[];
}

const ALL_SECTIONS: ManualSection[] = [
    {
        key: 'dashboard',
        icon: 'pi pi-gauge',
        title: 'Dashboard',
        subtitle: 'Panel principal con estadísticas y gráficos del día.',
        colorVar: '--p-blue-500',
        requiredRoles: [],
        screenElements: [
            {
                type: 'toolbar',
                title: 'Elementos de la pantalla',
                items: [
                    { icon: 'pi pi-chart-bar', label: 'Widgets de estadísticas', description: 'Tarjetas superiores con totales del día: órdenes generadas, completadas, facturación y pacientes atendidos.' },
                    { icon: 'pi pi-chart-line', label: 'Gráfico de evolución', description: 'Gráfico de líneas o barras con la evolución semanal/mensual de órdenes.' },
                    { icon: 'pi pi-table', label: 'Tabla de resumen', description: 'Listado rápido de las últimas órdenes o movimientos del día.' },
                    { icon: 'pi pi-user-edit', label: 'Acceso al perfil', description: 'Ícono de usuario en la barra superior derecha. Permite ver y editar los datos del usuario logueado y cambiar contraseña.' }
                ]
            }
        ],
        processes: [
            {
                title: 'Leer el dashboard',
                icon: 'pi pi-eye',
                steps: [
                    'Al iniciar sesión, el sistema redirige automáticamente al Dashboard.',
                    'Los widgets superiores muestran los totales del día en tiempo real.',
                    'El gráfico central muestra la tendencia histórica de órdenes.',
                    'Si los datos no se actualizan, presione F5 para recargar la página.'
                ],
                tip: 'El dashboard se recarga automáticamente. No es necesario refrescarlo manualmente a menos que note datos desactualizados.'
            },
            {
                title: 'Acceder al perfil de usuario',
                icon: 'pi pi-user',
                steps: [
                    'Haga clic en su nombre o avatar ubicado en la barra superior derecha.',
                    'Seleccione "Perfil" en el menú desplegable.',
                    'Edite sus datos personales y haga clic en Guardar.',
                    'Para cambiar su contraseña, ingrese la contraseña actual y luego la nueva dos veces.'
                ]
            }
        ]
    },
    {
        key: 'patients',
        icon: 'pi pi-users',
        title: 'Pacientes',
        subtitle: 'Registro y gestión de datos de los pacientes del sistema.',
        colorVar: '--p-green-500',
        requiredRoles: ['ROOT', 'ENCARGADO', 'RADIOLOGO', 'SECRETARIA', 'RRHH'],
        screenElements: [
            {
                type: 'toolbar',
                title: 'Barra de herramientas',
                items: [
                    { icon: 'pi pi-plus', label: 'Nuevo paciente', description: 'Abre el formulario modal para registrar un nuevo paciente.' },
                    { icon: 'pi pi-search', label: 'Buscador por CI o Nombre', description: 'Campo de texto para filtrar pacientes por número de cédula o nombre completo. Presione Buscar o Enter.' },
                    { icon: 'pi pi-calendar', label: 'Filtro por fecha de creación', description: 'Dos selectores de fecha (desde / hasta) para filtrar pacientes por fecha de registro.' }
                ]
            },
            {
                type: 'table',
                title: 'Tabla de pacientes',
                items: [
                    { label: 'CI', description: 'Número de cédula de identidad del paciente.' },
                    { label: 'Nombre', description: 'Nombre(s) del paciente.' },
                    { label: 'Apellido', description: 'Apellido(s) del paciente.' },
                    { label: 'Email', description: 'Correo electrónico de contacto.' },
                    { label: 'Teléfono', description: 'Número de teléfono o celular.' },
                    { label: 'Estado', description: 'Badge de color: Activo (verde) o Inactivo (rojo).' },
                    { label: 'Creado', description: 'Fecha de registro en el sistema.' },
                    { label: 'Acciones', description: 'Botón Editar (lápiz) para abrir el formulario de actualización.' }
                ]
            },
            {
                type: 'form',
                title: 'Formulario de paciente (modal)',
                items: [
                    { label: 'CI / Documento', description: 'Número de cédula de identidad. Campo obligatorio.' },
                    { label: 'Nombre', description: 'Nombre(s) del paciente. Obligatorio.' },
                    { label: 'Apellido', description: 'Apellido(s). Obligatorio.' },
                    { label: 'Fecha de nacimiento', description: 'Selector de fecha en formato dd/mm/aaaa.' },
                    { label: 'Género', description: 'Selector: Masculino / Femenino / Otro.' },
                    { label: 'Email', description: 'Correo electrónico. Se usa para enviar resultados.' },
                    { label: 'Teléfono', description: 'Número de contacto.' },
                    { label: 'Dirección', description: 'Dirección del domicilio.' },
                    { label: 'Estado', description: 'Activo / Inactivo.' }
                ]
            }
        ],
        processes: [
            {
                title: 'Registrar un nuevo paciente',
                icon: 'pi pi-user-plus',
                steps: [
                    'Vaya a Carga de Datos → Pacientes en el menú lateral.',
                    'Haga clic en el botón Nuevo paciente (ícono +) en la barra superior.',
                    'Complete los campos obligatorios: CI, Nombre, Apellido.',
                    'Ingrese email y teléfono para poder enviar resultados al paciente.',
                    'Haga clic en Guardar. El paciente quedará disponible al crear órdenes.'
                ],
                tip: 'Antes de registrar, busque por CI para evitar duplicados en el sistema.'
            },
            {
                title: 'Buscar y editar un paciente',
                icon: 'pi pi-search',
                steps: [
                    'En la pantalla de Pacientes, escriba el CI o nombre en el buscador.',
                    'Haga clic en Buscar o presione Enter.',
                    'Localice al paciente en la tabla y haga clic en el botón de edición (lápiz).',
                    'Modifique los datos necesarios en el formulario.',
                    'Haga clic en Actualizar para guardar los cambios.'
                ]
            }
        ]
    },
    {
        key: 'doctors',
        icon: 'pi pi-address-book',
        title: 'Médicos',
        subtitle: 'Registro de médicos derivantes asociados a las órdenes.',
        colorVar: '--p-teal-500',
        requiredRoles: ['ROOT', 'ENCARGADO', 'RADIOLOGO', 'SECRETARIA', 'RRHH'],
        screenElements: [
            {
                type: 'toolbar',
                title: 'Barra de herramientas',
                items: [
                    { icon: 'pi pi-plus', label: 'Nuevo doctor', description: 'Abre el formulario para registrar un nuevo médico derivante.' },
                    { icon: 'pi pi-search', label: 'Búsqueda por Nro. Registro o Nombre', description: 'Filtra la lista de médicos por número de registro profesional o nombre.' },
                    { icon: 'pi pi-calendar', label: 'Filtro por fecha', description: 'Rango de fechas para filtrar por fecha de creación del registro.' }
                ]
            },
            {
                type: 'table',
                title: 'Tabla de médicos',
                items: [
                    { label: 'CI', description: 'Número de cédula del médico.' },
                    { label: 'Nombre / Apellido', description: 'Nombre completo del médico.' },
                    { label: 'Registro', description: 'Número de registro profesional habilitante.' },
                    { label: 'Especialidad', description: 'Especialidad médica.' },
                    { label: 'Teléfono', description: 'Número de contacto.' },
                    { label: 'Estado', description: 'Activo (verde) / Inactivo (rojo).' },
                    { label: 'Acciones', description: 'Botón Editar.' }
                ]
            },
            {
                type: 'form',
                title: 'Formulario de médico',
                items: [
                    { label: 'Nombre / Apellido', description: 'Nombre completo. Obligatorio.' },
                    { label: 'Nro. de Registro', description: 'Número de matrícula profesional. Obligatorio.' },
                    { label: 'Especialidad', description: 'Especialidad médica.' },
                    { label: 'Género', description: 'Masculino / Femenino / Otro.' },
                    { label: 'Teléfono', description: 'Celular de contacto.' },
                    { label: 'Email', description: 'Correo para envío de resultados.' }
                ]
            }
        ],
        processes: [
            {
                title: 'Registrar un nuevo médico',
                icon: 'pi pi-user-plus',
                steps: ['Vaya a Carga de Datos → Doctores.', 'Haga clic en Nuevo doctor.', 'Complete nombre completo, número de registro y especialidad.', 'Ingrese email para poder enviar resultados al médico derivante.', 'Haga clic en Guardar.'],
                tip: 'El médico también puede registrarse desde el wizard de facturación al crear una orden.'
            }
        ]
    },
    {
        key: 'orders',
        icon: 'pi pi-receipt',
        title: 'Órdenes Clínicas',
        subtitle: 'Creación, seguimiento y resultados de órdenes de diagnóstico por imagen.',
        colorVar: '--p-orange-500',
        requiredRoles: ['ROOT', 'ENCARGADO', 'RADIOLOGO', 'SECRETARIA', 'RRHH'],
        screenElements: [
            {
                type: 'toolbar',
                title: 'Pantalla: Nueva Orden — Wizard',
                items: [
                    { icon: 'pi pi-list', label: 'Barra de pasos lateral', description: 'Panel izquierdo con 4 pasos: 1) Buscar Paciente, 2) Seleccionar Estudios, 3) Información de Pago, 4) Confirmación.' },
                    { icon: 'pi pi-search', label: 'Buscador de paciente por CI', description: 'Input de texto con mínimo 3 dígitos. Botón Buscar ejecuta la consulta.' },
                    { icon: 'pi pi-user-plus', label: 'Crear Nuevo Paciente', description: 'Botón verde que abre el formulario de registro rápido si el paciente no existe.' },
                    { icon: 'pi pi-arrow-right', label: 'Seleccionar paciente', description: 'Botón en cada fila del resultado de búsqueda para elegir al paciente de la orden.' }
                ]
            },
            {
                type: 'table',
                title: 'Pantalla: Listado de Órdenes — Tabla',
                items: [
                    { label: 'N° Orden', description: 'Número único de la orden generado automáticamente.' },
                    { label: 'Cédula / Nombre', description: 'CI y nombre completo del paciente.' },
                    { label: 'Fecha Nac. / Fecha', description: 'Fecha de nacimiento del paciente y fecha de creación de la orden.' },
                    { label: 'Estado', description: 'Badge: Pendiente (rojo) | En proceso (naranja) | Incompleto (naranja) | Completado (verde) | Cancelado (gris).' },
                    { label: 'Imagen', description: 'Indicador de archivos adjuntos a la orden.' },
                    { label: 'Acciones', description: 'Conjunto de botones de acción según el estado de la orden y el rol.' }
                ]
            },
            {
                type: 'toolbar',
                title: 'Pantalla: Listado — Acciones de archivo y factura',
                items: [
                    { icon: 'pi pi-image', label: 'Ver imágenes', description: 'Abre carrusel con todos los archivos adjuntos (imágenes, PDFs).' },
                    { icon: 'pi pi-file-plus', label: 'Adjuntar imagen', description: 'Abre área de drag & drop para cargar archivos (.jpg, .png, .pdf).' },
                    { icon: 'pi pi-file-check', label: 'Ver Factura', description: 'Abre el PDF de la factura emitida. Solo visible si la orden tiene factura.' },
                    { icon: 'pi pi-print', label: 'Imprimir Factura', description: 'Abre el wizard de facturación. Visible si la orden tiene monto y no tiene factura.' },
                    { icon: 'pi pi-ticket', label: 'Imprimir etiqueta', description: 'Genera e imprime la etiqueta de muestra de la orden.' }
                ]
            },
            {
                type: 'toolbar',
                title: 'Pantalla: Listado — Acciones de gestión',
                requiredRoles: ['ROOT', 'ENCARGADO', 'RADIOLOGO', 'RRHH'],
                items: [
                    { icon: 'pi pi-times-circle', label: 'Cancelar orden', description: 'Abre el modal de cancelación con selector de tipo y motivo obligatorio. No disponible para SECRETARIA.' },
                    { icon: 'pi pi-send', label: 'Enviar por email', description: 'Envía los resultados al email del médico y/o paciente. Disponible si tiene factura.' },
                    { icon: 'pi pi-history', label: 'Historial de envíos', description: 'Muestra tabla con todos los intentos de envío: fecha, hora, estado y destino.' }
                ]
            },
            {
                type: 'table',
                title: 'Pantalla: Órdenes Pendientes — Tabla expandible',
                requiredRoles: ['ROOT', 'ENCARGADO', 'RADIOLOGO', 'RRHH'],
                items: [
                    { label: 'N° Orden / Cédula / Nombre', description: 'Datos de identificación de la orden.' },
                    { label: 'Edad', description: 'Edad calculada del paciente.' },
                    { label: 'Prioridad', description: 'Badge: Urgente (rojo) | Normal (verde).' },
                    { label: 'Expansión de estudios', description: 'Al hacer clic en la fila, se expande para mostrar estudios individuales con estado y acciones: Play (iniciar), Stop (completar), Replay (repetir).' },
                    { label: 'Completar orden', description: 'Botón verde si todos los estudios están finalizados, naranja si hay estudios incompletos.' }
                ]
            },
            {
                type: 'modal',
                title: 'Modales — Generales',
                items: [
                    { label: 'Visor de archivos', description: 'Carrusel con zoom (+/-) para visualizar imágenes y PDFs de la orden.' },
                    { label: 'Cargar resultado', description: 'Drag & drop para subir archivos. Acepta .jpg, .jpeg, .png, .pdf.' },
                    { label: 'Wizard de Facturación', description: '3 pasos: 1) Seleccionar Doctor, 2) Datos de facturación (RUC, nombre), 3) Métodos de pago.' },
                    { label: 'Crear Doctor', description: 'Formulario rápido: Nombre, Apellido, Nro Registro, Especialidad, Género, Teléfono, Email.' }
                ]
            },
            {
                type: 'modal',
                title: 'Modales — Gestión avanzada',
                requiredRoles: ['ROOT', 'ENCARGADO', 'RADIOLOGO', 'RRHH'],
                items: [
                    { label: 'Cancelar Orden', description: 'Selector de tipo de cancelación y campo de motivo obligatorio.' },
                    { label: 'Enviar Factura', description: 'Campo de email destino con posibilidad de editar el destinatario.' },
                    { label: 'Historial de Envíos', description: 'Tabla: Fecha | Hora | Estado | Destino | Asunto | Error (si hubo fallo).' }
                ]
            }
        ],
        processes: [
            {
                title: 'Crear una nueva orden',
                icon: 'pi pi-plus-circle',
                steps: [
                    'Vaya a Atención - Ordenes → Ordenes → Nueva.',
                    'Paso 1 — Busque al paciente ingresando su CI (mínimo 3 dígitos) y haga clic en Buscar.',
                    'Si el paciente no existe, haga clic en "Crear Nuevo Paciente" y complete sus datos.',
                    'Haga clic en el botón → de la fila del paciente para seleccionarlo.',
                    'Paso 2 — Seleccione los estudios solicitados de la lista disponible.',
                    'Paso 3 — Indique la modalidad de pago: contado, seguro, etc.',
                    'Paso 4 — Revise el resumen y haga clic en Confirmar Orden.',
                    'El sistema asigna automáticamente un número de orden.'
                ],
                tip: 'Verifique que el paciente y el médico existan antes de iniciar la orden para evitar demoras.'
            },
            {
                title: 'Gestionar estudios pendientes',
                icon: 'pi pi-play',
                requiredRoles: ['ROOT', 'ENCARGADO', 'RADIOLOGO', 'RRHH'],
                steps: [
                    'Vaya a Atención - Ordenes → Ordenes → Generadas.',
                    'Localice la orden en la tabla y haga clic sobre ella para expandirla.',
                    'Para cada estudio: haga clic en Play (▶) para marcarlo como "En proceso".',
                    'Una vez realizado el estudio, haga clic en Stop (⏹) para marcarlo como "Completado".',
                    'Si necesita repetir un estudio, use el botón Replay (↺) e ingrese el motivo obligatorio.',
                    'Cuando todos los estudios estén completos, haga clic en "Completar orden".'
                ],
                warn: 'Completar la orden es irreversible. Asegúrese de que todos los resultados estén cargados antes de confirmar.'
            },
            {
                title: 'Adjuntar y enviar resultados',
                icon: 'pi pi-send',
                requiredRoles: ['ROOT', 'ENCARGADO', 'RADIOLOGO'],
                steps: [
                    'En el Listado, localice la orden y haga clic en el ícono de archivo (📎).',
                    'Arrastre los archivos al área de carga o haga clic para seleccionarlos.',
                    'El sistema acepta .jpg, .png y .pdf.',
                    'Para enviar por email, haga clic en el ícono Enviar (✉). Verifique el email destino y confirme.',
                    'El historial de envíos (🕐) muestra el estado de cada intento.'
                ],
                tip: 'Puede adjuntar múltiples archivos en una sola carga.'
            },
            {
                title: 'Facturar una orden',
                icon: 'pi pi-file-edit',
                requiredRoles: ['ROOT', 'ENCARGADO', 'SECRETARIA', 'RRHH'],
                steps: [
                    'En el Listado de Órdenes, localice la orden y haga clic en el ícono de impresión (🖨).',
                    'Paso 1 — Seleccione el médico derivante o créelo si no existe.',
                    'Paso 2 — Ingrese el RUC y razón social del cliente, o marque "Consumidor Final".',
                    'Paso 3 — Seleccione el/los métodos de pago e ingrese los montos.',
                    'Confirme para generar la factura. Puede imprimirla o enviarla por email.'
                ],
                warn: 'Verifique que el timbrado esté vigente antes de emitir la factura.'
            }
        ]
    },
    {
        key: 'laboratory',
        icon: 'pi pi-chart-scatter',
        title: 'Órdenes de Laboratorio',
        subtitle: 'Gestión de análisis clínicos de laboratorio.',
        colorVar: '--p-purple-500',
        requiredRoles: ['ROOT', 'ENCARGADO', 'RADIOLOGO', 'SECRETARIA', 'RRHH'],
        screenElements: [
            {
                type: 'toolbar',
                title: 'Pantalla: Nueva Orden de Laboratorio',
                items: [
                    { icon: 'pi pi-list', label: 'Wizard de 4 pasos', description: 'Igual estructura que Órdenes Clínicas: Paciente → Estudios → Pago → Confirmación.' },
                    { icon: 'pi pi-tag', label: 'Imprimir etiqueta de muestra', description: 'Disponible tras confirmar la orden. Genera etiqueta con código de barras para la muestra biológica.' }
                ]
            },
            {
                type: 'table',
                title: 'Pantalla: Órdenes Pendientes de Laboratorio',
                requiredRoles: ['ROOT', 'ENCARGADO', 'RADIOLOGO', 'RRHH'],
                items: [
                    { label: 'Estado por estudio', description: 'Cada estudio tiene estado propio: PENDING_LAB (rojo), IN_PROGRESS_LAB (naranja), COMPLETED_LAB (verde), SENT_LAB (azul).' },
                    { label: 'Tablero de Control', description: 'Vista exclusiva de sucursal de laboratorio. Muestra todas las órdenes del día en tiempo real con colores: amarillo = pendiente, azul = en proceso, verde = completado.' }
                ]
            },
            {
                type: 'modal',
                title: 'Modales de laboratorio',
                requiredRoles: ['ROOT', 'ENCARGADO', 'RADIOLOGO', 'RRHH'],
                items: [
                    { label: 'Carga de valores', description: 'Formulario con los campos de cada análisis (ej: hemoglobina, glucosa). El sistema muestra rangos de referencia junto a cada campo.' },
                    { label: 'Tablero de Control', description: 'Panel en tiempo real. Cada tarjeta muestra: paciente, estudios y estado. Se actualiza automáticamente.' }
                ]
            }
        ],
        processes: [
            {
                title: 'Crear orden de laboratorio',
                icon: 'pi pi-plus-circle',
                steps: [
                    'Vaya a Atención - Ordenes → Ordenes - Laboratorio → Nueva.',
                    'Siga el mismo wizard de 4 pasos que en Órdenes Clínicas.',
                    'Tras confirmar, imprima la etiqueta de la muestra para identificarla.',
                    'La orden aparecerá en la bandeja de Pendientes de la sucursal de laboratorio.'
                ]
            },
            {
                title: 'Procesar y cargar resultados de laboratorio',
                icon: 'pi pi-check-circle',
                requiredRoles: ['ROOT', 'ENCARGADO', 'RADIOLOGO', 'RRHH'],
                steps: [
                    'En Órdenes - Laboratorio → Generadas, localice la orden.',
                    'Haga clic en Play para iniciar el procesamiento de cada análisis.',
                    'Cuando tenga los resultados, vaya a Resultados y seleccione la orden.',
                    'Complete los valores de cada análisis en el formulario.',
                    'El sistema indica con color si el valor está fuera del rango de referencia.',
                    'Guarde y confirme para marcar la orden como completada.'
                ],
                tip: 'Use el Tablero de Control para una vista rápida de todos los estados del día.'
            }
        ]
    },
    {
        key: 'history',
        icon: 'pi pi-history',
        title: 'Historial',
        subtitle: 'Consulta del historial de órdenes por paciente o por médico.',
        colorVar: '--p-cyan-500',
        requiredRoles: [],
        screenElements: [
            {
                type: 'toolbar',
                title: 'Pantalla: Historial de Pacientes',
                items: [
                    { icon: 'pi pi-search', label: 'Búsqueda por CI o Nombre', description: 'Input que filtra la tabla de pacientes en tiempo real.' },
                    { icon: 'pi pi-calendar', label: 'Filtro por fecha de orden', description: 'Rango de fechas para filtrar órdenes dentro del historial del paciente.' }
                ]
            },
            {
                type: 'table',
                title: 'Tabla de Historial de Pacientes (expandible)',
                items: [
                    { label: 'CI / Nombre / Apellido', description: 'Datos identificatorios del paciente.' },
                    { label: 'Teléfono', description: 'Contacto del paciente.' },
                    { label: 'Fecha Nac. / Edad', description: 'Edad calculada automáticamente.' },
                    { label: 'Cant. Órdenes', description: 'Badge azul con el total de órdenes del paciente.' },
                    { label: 'Expansión', description: 'Al hacer clic en la fila, muestra tabla anidada con: N° Orden | Fecha | Estado | Doctor | Sucursal.' }
                ]
            },
            {
                type: 'table',
                title: 'Tabla de Historial de Doctores (expandible)',
                items: [
                    { label: 'CI / Nombre / Registro / Celular', description: 'Datos del médico.' },
                    { label: 'Cant. Órdenes', description: 'Total de órdenes derivadas por el médico.' },
                    { label: 'Expansión', description: 'Muestra: N° Orden | Fecha | Paciente | CI Paciente | Sucursal | Seguro | Botón ver archivos.' }
                ]
            }
        ],
        processes: [
            {
                title: 'Consultar historial de un paciente',
                icon: 'pi pi-user',
                steps: [
                    'Vaya a Atención - Ordenes → Historial → Pacientes.',
                    'Escriba el CI o nombre del paciente en el buscador.',
                    'Haga clic en la fila del paciente para expandir su historial de órdenes.',
                    'Use el filtro de fechas para acotar el rango de órdenes mostradas.'
                ]
            },
            {
                title: 'Consultar historial de un médico',
                icon: 'pi pi-address-book',
                steps: [
                    'Vaya a Atención - Ordenes → Historial → Doctores.',
                    'Aplique el filtro de fechas para el período que necesita consultar.',
                    'Haga clic en la fila del médico para ver todas sus órdenes.',
                    'Desde la expansión puede ver archivos adjuntos de cada orden.'
                ]
            }
        ]
    },
    {
        key: 'cashier',
        icon: 'pi pi-wallet',
        title: 'Caja',
        subtitle: 'Apertura, movimientos y cierre de caja al inicio y fin de turno.',
        colorVar: '--p-yellow-600',
        requiredRoles: ['ROOT', 'ENCARGADO', 'RADIOLOGO', 'SECRETARIA', 'RRHH'],
        screenElements: [
            {
                type: 'toolbar',
                title: 'Pantalla: Apertura de Caja',
                items: [
                    { icon: 'pi pi-desktop', label: 'Selección de caja', description: 'Panel con botones para elegir la caja física asignada al turno.' },
                    { icon: 'pi pi-table', label: 'Conteo de efectivo inicial', description: 'Tabla con denominaciones de billetes y monedas. Ingrese la cantidad de cada denominación para calcular el fondo de caja.' },
                    { icon: 'pi pi-file-edit', label: 'Observaciones', description: 'Área de texto para notas de la apertura (opcional).' },
                    { icon: 'pi pi-check', label: 'Confirmar Apertura', description: 'Botón que registra la apertura y habilita la caja para el turno.' }
                ]
            },
            {
                type: 'toolbar',
                title: 'Pantalla: Caja Activa — Panel general',
                items: [
                    { icon: 'pi pi-chart-bar', label: 'Card Caja / Sucursal', description: 'Muestra el nombre de la caja abierta y la sucursal.' },
                    { icon: 'pi pi-money-bill', label: 'Card Apertura', description: 'Monto total declarado al abrir la caja en cada divisa.' },
                    { icon: 'pi pi-calculator', label: 'Card Balance Esperado', description: 'Total calculado por el sistema: apertura + ingresos - egresos.' },
                    { icon: 'pi pi-credit-card', label: 'Card Ingresos por Medio de Pago', description: 'Desglose de ingresos por efectivo, tarjeta, transferencia, etc.' },
                    { icon: 'pi pi-refresh', label: 'Refrescar', description: 'Actualiza los totales y la tabla de movimientos.' },
                    { icon: 'pi pi-lock', label: 'Proceder al Cierre', description: 'Inicia el proceso de cierre de caja. Requiere confirmación.' }
                ]
            },
            {
                type: 'toolbar',
                title: 'Pantalla: Caja Activa — Registro manual',
                requiredRoles: ['ROOT'],
                items: [{ icon: 'pi pi-plus', label: 'Registrar Movimiento', description: 'Exclusivo ROOT. Abre formulario para ingresar o egresar dinero manualmente fuera del flujo de órdenes.' }]
            },
            {
                type: 'table',
                title: 'Tabla de movimientos',
                items: [
                    { label: 'Fecha / Hora', description: 'Timestamp del movimiento.' },
                    { label: 'Tipo', description: 'Ingreso o Egreso.' },
                    { label: 'Categoría', description: 'Categoría del movimiento (cobro de orden, depósito, gasto, etc.).' },
                    { label: 'Método de pago', description: 'Efectivo, tarjeta, transferencia, etc.' },
                    { label: 'Monto', description: 'Valor del movimiento en la divisa correspondiente.' },
                    { label: 'Descripción', description: 'Concepto o detalle del movimiento.' }
                ]
            },
            {
                type: 'toolbar',
                title: 'Pantalla: Cierre de Caja',
                items: [
                    { icon: 'pi pi-table', label: 'Conteo de efectivo al cierre', description: 'Misma tabla de denominaciones que en la apertura. Ingrese el efectivo físico contado.' },
                    { icon: 'pi pi-chart-bar', label: 'Comparación sistema vs. declarado', description: 'El sistema calcula automáticamente la diferencia entre el balance esperado y lo declarado.' },
                    { icon: 'pi pi-file-edit', label: 'Justificación', description: 'Si hay diferencia, este campo es obligatorio.' },
                    { icon: 'pi pi-send', label: 'Confirmar Cierre', description: 'Envía el cierre a estado "Pendiente de Aprobación".' }
                ]
            }
        ],
        processes: [
            {
                title: 'Abrir caja al inicio del turno',
                icon: 'pi pi-sign-in',
                steps: [
                    'Vaya a Cajeros → Caja en el menú lateral.',
                    'Si la caja no está abierta, el sistema muestra la pantalla de Apertura.',
                    'Seleccione la caja física asignada a su puesto.',
                    'Cuente el efectivo del fondo de caja e ingrese la cantidad de cada denominación.',
                    'Agregue observaciones si lo considera necesario.',
                    'Haga clic en Confirmar Apertura para iniciar la sesión de caja.'
                ]
            },
            {
                title: 'Registrar un movimiento manual',
                icon: 'pi pi-arrows-h',
                requiredRoles: ['ROOT'],
                steps: ['Con la caja abierta, haga clic en Registrar Movimiento.', 'Seleccione el tipo: Ingreso o Egreso.', 'Elija la categoría y el método de pago.', 'Ingrese el monto y una descripción del concepto.', 'Confirme el movimiento.'],
                tip: 'El movimiento quedará registrado inmediatamente en la tabla de la caja activa.'
            },
            {
                title: 'Cerrar caja al finalizar el turno',
                icon: 'pi pi-sign-out',
                steps: [
                    'Con la caja activa, haga clic en Proceder al Cierre.',
                    'Cuente el efectivo físico en caja e ingrese las denominaciones.',
                    'El sistema compara automáticamente con el balance esperado.',
                    'Si hay diferencia, ingrese el motivo en el campo de justificación.',
                    'Haga clic en Confirmar Cierre.',
                    'El estado pasa a "Pendiente de Aprobación" hasta que el ENCARGADO lo revise.'
                ],
                warn: 'Una vez cerrada, la caja no puede reabrirse sin autorización del ENCARGADO o ROOT. Verifique los montos antes de confirmar.'
            }
        ]
    },
    {
        key: 'approval',
        icon: 'pi pi-check-square',
        title: 'Aprobación de Cajas',
        subtitle: 'Revisión y aprobación de cierres enviados por los cajeros.',
        colorVar: '--p-red-500',
        requiredRoles: ['ROOT', 'ENCARGADO', 'RADIOLOGO'],
        screenElements: [
            {
                type: 'table',
                title: 'Bandeja de cierres pendientes',
                items: [
                    { label: 'Caja / Cajero', description: 'Nombre de la caja y usuario que realizó el cierre.' },
                    { label: 'Sucursal', description: 'Sucursal a la que pertenece la caja.' },
                    { label: 'Fecha / Hora apertura', description: 'Momento en que se abrió la caja.' },
                    { label: 'Fecha / Hora cierre', description: 'Momento del cierre declarado.' },
                    { label: 'Estado', description: 'Pendiente de Aprobación (naranja).' },
                    { label: 'Acciones', description: 'Botón Ver Detalle para revisar el cierre completo.' }
                ]
            },
            {
                type: 'toolbar',
                title: 'Pantalla: Detalle del cierre',
                items: [
                    { icon: 'pi pi-chart-bar', label: 'Resumen de totales', description: 'Balance del sistema, monto declarado y diferencia por divisa y método de pago.' },
                    { icon: 'pi pi-list', label: 'Movimientos del turno', description: 'Lista completa de todos los movimientos registrados durante el turno.' },
                    { icon: 'pi pi-check', label: 'Aprobar', description: 'Aprueba el cierre. El estado pasa a "Aprobado" y queda disponible para verificación contable.' },
                    { icon: 'pi pi-times', label: 'Rechazar', description: 'Rechaza el cierre. Requiere ingresar el motivo. El cajero deberá corregir y reenviar.' }
                ]
            }
        ],
        processes: [
            {
                title: 'Aprobar un cierre de caja',
                icon: 'pi pi-check',
                steps: [
                    'Vaya a Cajeros → Aprobación de cajas.',
                    'La bandeja muestra todos los cierres con estado "Pendiente".',
                    'Haga clic en Ver Detalle en el cierre a revisar.',
                    'Revise los totales: balance del sistema vs. monto declarado.',
                    'Revise la lista de movimientos del turno.',
                    'Si todo es correcto, haga clic en Aprobar.',
                    'Si hay inconsistencias, haga clic en Rechazar e ingrese el motivo.'
                ],
                tip: 'Preste especial atención a los movimientos manuales y a las diferencias de efectivo.'
            }
        ]
    },
    {
        key: 'verification',
        icon: 'pi pi-shield',
        title: 'Verificación de Cajas',
        subtitle: 'Auditoría contable de cajas previamente aprobadas.',
        colorVar: '--p-indigo-500',
        requiredRoles: ['ROOT', 'ADMIN', 'CONTADOR', 'RRHH'],
        screenElements: [
            {
                type: 'filter',
                title: 'Filtros disponibles',
                items: [
                    { icon: 'pi pi-calendar', label: 'Rango de fechas', description: 'Filtra los cierres aprobados por fecha.' },
                    { icon: 'pi pi-building', label: 'Sucursal', description: 'Filtra por sucursal específica.' }
                ]
            },
            {
                type: 'table',
                title: 'Tabla de cierres aprobados',
                items: [
                    { label: 'Caja / Cajero / Sucursal', description: 'Identificación completa del cierre.' },
                    { label: 'Fecha apertura y cierre', description: 'Rango del turno.' },
                    { label: 'Total sistema', description: 'Balance calculado por el sistema.' },
                    { label: 'Total declarado', description: 'Monto declarado por el cajero.' },
                    { label: 'Diferencia', description: 'Variación entre sistema y declarado.' },
                    { label: 'Estado', description: 'Aprobado (verde) o Ya verificado (azul).' },
                    { label: 'Acciones', description: 'Ver Detalle — Verificar.' }
                ]
            }
        ],
        processes: [
            {
                title: 'Verificar una caja aprobada',
                icon: 'pi pi-shield',
                steps: [
                    'Vaya a Cajeros → Verificación de cajas.',
                    'Use los filtros de fecha y sucursal para localizar el cierre a verificar.',
                    'Haga clic en Ver Detalle para revisar los movimientos y comprobantes.',
                    'Contraste los totales con los comprobantes físicos.',
                    'Haga clic en Verificar para confirmar la auditoría.',
                    'Puede exportar el reporte en Excel desde la bandeja.'
                ],
                warn: 'La verificación es el paso final del flujo. Una vez verificado, el registro queda bloqueado para edición.'
            }
        ]
    },
    {
        key: 'collections',
        icon: 'pi pi-dollar',
        title: 'Cobranzas',
        subtitle: 'Seguimiento de cobros pendientes y verificación de pagos.',
        colorVar: '--p-lime-600',
        requiredRoles: ['ROOT', 'ADMIN'],
        screenElements: [
            {
                type: 'filter',
                title: 'Filtros — Pendientes de Cobro',
                items: [
                    { icon: 'pi pi-calendar', label: 'Mes', description: 'Selector de mes/año (mm/aaaa) para filtrar por período de facturación.' },
                    { icon: 'pi pi-building-columns', label: 'Seguro', description: 'Selector con búsqueda para filtrar por entidad aseguradora.' },
                    { icon: 'pi pi-building', label: 'Sucursal del seguro', description: 'Filtra por sucursal específica del seguro.' },
                    { icon: 'pi pi-list', label: 'Tipo', description: 'Tipo de cobranza.' }
                ]
            },
            {
                type: 'table',
                title: 'Tabla de pendientes',
                items: [
                    { label: 'N° Orden', description: 'Número de la orden pendiente.' },
                    { label: 'Paciente / CI / Médico', description: 'Datos del servicio.' },
                    { label: 'Sucursal', description: 'Sucursal donde se generó la orden.' },
                    { label: 'Fecha', description: 'Fecha de la orden.' },
                    { label: 'Verificación', description: 'Badge: Verificado (verde) | Pendiente (naranja).' },
                    { label: 'Acción', description: 'Botón Verificar (info) o Verificado (success).' }
                ]
            }
        ],
        processes: [
            {
                title: 'Gestionar pendientes de cobro',
                icon: 'pi pi-clock',
                steps: [
                    'Vaya a Cobranzas → Pendientes de Cobro.',
                    'Seleccione el mes/año y el seguro a gestionar.',
                    'La tabla lista todas las órdenes pendientes de cobro para ese período.',
                    'Haga clic en Verificar al confirmar que se cobró el servicio.',
                    'El registro pasa al listado de Verificados.'
                ]
            }
        ]
    },
    {
        key: 'reports',
        icon: 'pi pi-chart-bar',
        title: 'Reportes',
        subtitle: 'Informes gerenciales de facturación y producción.',
        colorVar: '--p-sky-500',
        requiredRoles: ['ROOT', 'ADMIN'],
        screenElements: [
            {
                type: 'filter',
                title: 'Filtros comunes en reportes',
                items: [
                    { icon: 'pi pi-calendar', label: 'Rango de fechas', description: 'Fecha inicio y fecha fin para el período del reporte.' },
                    { icon: 'pi pi-building', label: 'Sucursal', description: 'Filtra por sucursal específica o todas.' },
                    { icon: 'pi pi-address-book', label: 'Médico', description: 'Filtra órdenes de un médico específico.' },
                    { icon: 'pi pi-file-excel', label: 'Exportar a Excel', description: 'Descarga el reporte filtrado en formato .xlsx.' }
                ]
            },
            {
                type: 'table',
                title: 'Reporte: Órdenes Facturadas',
                items: [
                    { label: 'N° Orden', description: 'Número identificador.' },
                    { label: 'Paciente / CI', description: 'Datos del paciente.' },
                    { label: 'Médico', description: 'Médico derivante.' },
                    { label: 'Fecha', description: 'Fecha de la orden.' },
                    { label: 'Estado de Facturación', description: 'Facturado / Sin factura.' },
                    { label: 'Monto', description: 'Valor total de la orden.' }
                ]
            },
            {
                type: 'table',
                title: 'Reporte: Totales Diarios',
                items: [
                    { label: 'Fecha', description: 'Día del resumen.' },
                    { label: 'Sucursal', description: 'Sucursal del movimiento.' },
                    { label: 'Método de pago', description: 'Efectivo, tarjeta, transferencia, seguro.' },
                    { label: 'Total', description: 'Monto total por método de pago en el día.' }
                ]
            }
        ],
        processes: [
            {
                title: 'Generar reporte de órdenes facturadas',
                icon: 'pi pi-receipt',
                steps: [
                    'Vaya a Secciones → Reportes → Órdenes Facturadas.',
                    'Seleccione el rango de fechas.',
                    'Opcionalmente filtre por sucursal o médico.',
                    'La tabla se actualiza automáticamente con los resultados.',
                    'Haga clic en Exportar Excel para descargar el reporte.'
                ]
            }
        ]
    },
    {
        key: 'settings',
        icon: 'pi pi-cog',
        title: 'Configuraciones',
        subtitle: 'Parámetros globales del sistema. Acceso exclusivo ROOT.',
        colorVar: '--p-zinc-500',
        requiredRoles: ['ROOT'],
        screenElements: [
            {
                type: 'toolbar',
                title: 'Gestión de Estudios',
                items: [
                    { icon: 'pi pi-clone', label: 'Nuevo Estudio', description: 'Abre formulario para registrar un estudio: Código, Nombre, Categoría, Tipo y precio base.' },
                    { icon: 'pi pi-pencil', label: 'Editar', description: 'Modifica los datos del estudio. El historial de precios se conserva.' },
                    { icon: 'pi pi-download', label: 'Descargar', description: 'Exporta el listado de estudios.' }
                ]
            },
            {
                type: 'toolbar',
                title: 'Gestión de Roles',
                items: [
                    { icon: 'pi pi-shield', label: 'Roles del sistema', description: 'Lista de roles configurados. Cada rol tiene un badge azul con su nombre.' },
                    { icon: 'pi pi-pencil', label: 'Gestionar permisos', description: 'Abre la pantalla de asignación de permisos por módulo para el rol seleccionado.' }
                ]
            },
            {
                type: 'toolbar',
                title: 'Timbrados',
                items: [
                    { icon: 'pi pi-plus', label: 'Nuevo Timbrado', description: 'Registra número de timbrado, serie, fecha de inicio y vencimiento habilitados por la SET.' },
                    { icon: 'pi pi-toggle-on', label: 'Activar / Desactivar', description: 'Controla qué timbrados están disponibles para emitir facturas.' }
                ]
            },
            {
                type: 'toolbar',
                title: 'Sucursales',
                items: [
                    { icon: 'pi pi-building', label: 'Nueva Sucursal', description: 'Formulario: Código (3 dígitos), Nombre, Ciudad, Teléfono, Email, Dirección.' },
                    { icon: 'pi pi-toggle-on', label: 'Estado', description: 'Activa o desactiva la sucursal en el sistema.' }
                ]
            },
            {
                type: 'toolbar',
                title: 'Envío Masivo',
                items: [{ icon: 'pi pi-send', label: 'Envío masivo de resultados', description: 'Seleccione rango de fechas y órdenes. El sistema envía los resultados por email a médicos y pacientes en lote.' }]
            }
        ],
        processes: [
            {
                title: 'Registrar un nuevo estudio',
                icon: 'pi pi-ticket',
                steps: ['Vaya a EXTRAS → Configuraciones → Estudios.', 'Haga clic en Nueva Estudio.', 'Ingrese: Código único, Nombre, Categoría, Tipo y precio.', 'Guarde. El estudio quedará disponible al crear órdenes.'],
                tip: 'El historial de precios se mantiene automáticamente para auditoría.'
            },
            {
                title: 'Gestionar timbrados',
                icon: 'pi pi-receipt',
                steps: [
                    'Vaya a Configuraciones → Timbrados.',
                    'Haga clic en Nuevo para registrar un timbrado habilitado por la SET.',
                    'Ingrese número, serie, fecha de inicio y fecha de vencimiento.',
                    'Active el timbrado para que esté disponible en facturación.',
                    'Desactive los timbrados vencidos para evitar su uso.'
                ],
                warn: 'Solo puede haber un timbrado activo por vez. Verifique las fechas antes de activar uno nuevo.'
            },
            {
                title: 'Asignar permisos a un rol',
                icon: 'pi pi-shield',
                steps: [
                    'Vaya a Configuraciones → Roles.',
                    'Haga clic en el botón de edición del rol a modificar.',
                    'En la pantalla de permisos, active o desactive los módulos y acciones.',
                    'Guarde los cambios. Los usuarios con ese rol verán los cambios al volver a iniciar sesión.'
                ],
                warn: 'Los cambios de permisos afectan a todos los usuarios con ese rol de forma inmediata.'
            }
        ]
    }
];

@Component({
    selector: 'app-manual',
    imports: [CommonModule, FormsModule, DrawerModule, TabsModule, TagModule, ButtonModule, DividerModule, InputTextModule, IconFieldModule, InputIconModule],
    templateUrl: './manual.component.html',
    styleUrl: './manual.component.scss'
})
export class ManualComponent {
    private auth = inject(AuthService);

    roles = this.auth.getUserRoles();
    user = this.auth.getCurrentUser();

    searchQuery = '';
    drawerVisible = false;
    selectedSection: ManualSection | null = null;

    private visibleSections: ManualSection[] = ALL_SECTIONS.filter((s) => s.requiredRoles.length === 0 || s.requiredRoles.some((r) => this.roles.includes(r)));

    get searchedSections(): ManualSection[] {
        const q = this.searchQuery.trim().toLowerCase();
        if (!q) return this.visibleSections;
        return this.visibleSections.filter((s) => s.title.toLowerCase().includes(q) || s.subtitle.toLowerCase().includes(q));
    }

    get currentElements(): ScreenElement[] {
        return (this.selectedSection?.screenElements ?? []).filter((e) => !e.requiredRoles?.length || e.requiredRoles.some((r) => this.roles.includes(r)));
    }

    get currentProcesses(): ManualProcess[] {
        return (this.selectedSection?.processes ?? []).filter((p) => !p.requiredRoles?.length || p.requiredRoles.some((r) => this.roles.includes(r)));
    }

    roleLabels: Record<string, string> = {
        ROOT: 'ROOT',
        Encargado: 'ENCARGADO',
        Radiólogo: 'RADIOLOGO',
        Secretaria: 'SECRETARIA',
        Administrador: 'ADMIN',
        Contador: 'CONTADOR',
        'Recursos Humanos': 'RRHH'
    };

    roleSeverity(role: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' {
        const m: Record<string, 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast'> = {
            Root: 'danger',
            Encargado: 'warn',
            RADIOLOGO: 'info',
            Secretaria: 'success',
            Administrador: 'secondary',
            Contador: 'contrast',
            'Recursos Humanos': 'info'
        };
        return m[role] ?? 'secondary';
    }

    elementTypeLabel(type: string): string {
        const m: Record<string, string> = {
            toolbar: 'Acciones y controles',
            table: 'Tabla de datos',
            filter: 'Filtros',
            form: 'Formulario',
            modal: 'Ventanas modales',
            status: 'Estados'
        };
        return m[type] ?? type;
    }

    elementTypeIcon(type: string): string {
        const m: Record<string, string> = {
            toolbar: 'pi pi-wrench',
            table: 'pi pi-table',
            filter: 'pi pi-filter',
            form: 'pi pi-file-edit',
            modal: 'pi pi-window-maximize',
            status: 'pi pi-tag'
        };
        return m[type] ?? 'pi pi-info-circle';
    }

    openSection(section: ManualSection): void {
        this.selectedSection = section;
        this.drawerVisible = true;
    }
}
